import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { uploadAudioAsync, deleteAudioFile } from '../../services/trackService';
import { getApiUrl, API_CONFIG } from '../../config/api';
import { resolveTrackMediaUrl } from '../../services/mediaAccess';
import { FaLink } from 'react-icons/fa';
import { Check, Star, X, Pause, Play, Square, MessageCircle, ChevronRight, Archive, Download } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { logInfo, logError } from '../../services/logtail';
import AudioWavePlayer from '../../components/AudioWavePlayer';
import CassetteLoader from '../../components/CassetteLoader';
import UploadProgressDisplay from '../../components/UploadProgressDisplay';
import FileUpload from '../../components/FileUpload';

import { getAuth } from 'firebase/auth';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useWebSocketNotifications } from '../../hooks/useWebSocketNotifications';
import { listComments } from '../../services/audioCommentService';
import { UPLOAD_FILE_ACCEPT, filterFilesForCategory } from '../../utils/security';
import { audioCommentsPagePath } from '../../utils/appRoutes';
import type { TrackPermissions } from '../../types/track';
import BinaryUnavailablePlaceholder from '../../components/BinaryUnavailablePlaceholder';
import type { BinaryInactiveReason } from '../../components/BinaryUnavailablePlaceholder';

interface AudioFile {
  id: string;
  name: string;
  fileId: string;
  isPreview?: boolean;
}

interface AudioFilesSectionProps {
  trackId: string;
  audioFiles: AudioFile[];
  onAudioFilesChange: (files: AudioFile[]) => void;
  audioFolderUrl?: string;
  permissions?: TrackPermissions;
  binaryAccess?: boolean;
  binaryInactiveReason?: BinaryInactiveReason;
  requiredStorageProvider?: string;
  viewerRole?: 'owner' | 'collaborator';
  storageProvider?: string;
}

const AudioFilesSection: React.FC<AudioFilesSectionProps> = ({
  trackId,
  audioFiles,
  onAudioFilesChange,
  audioFolderUrl,
  permissions,
  binaryAccess,
  binaryInactiveReason,
  requiredStorageProvider,
  viewerRole = 'collaborator',
  storageProvider,
}) => {
  const { t } = useTranslation();
  const { user, unlinkGoogleDrive } = useAuth();
  const navigate = useNavigate();
  const [isUploading, setIsUploading] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploadingFiles, setUploadingFiles] = useState<Set<string>>(new Set());
  const [uploadProgress, setUploadProgress] = useState<Record<string, { filename: string; progress: number }>>({});
  const [uploadFilenames, setUploadFilenames] = useState<Record<string, string>>({});
  const [uploadFileStatuses, setUploadFileStatuses] = useState<Record<string, 'uploading' | 'completed' | 'failed'>>({});
  const [deletingFiles, setDeletingFiles] = useState<Set<string>>(new Set());
  const [commentCounts, setCommentCounts] = useState<Record<string, { total: number; unresolved: number }>>({});
  
  // Audio player state - simplified to 3 states
  const [playingIdx, setPlayingIdx] = useState<number | null>(null); // null = not playing, number = playing this file
  const [isPaused, setIsPaused] = useState(false); // only relevant when playingIdx !== null
  const [blobUrl, setBlobUrl] = useState<string>('');
  const [loadingIdx, setLoadingIdx] = useState<number | null>(null);
  const [waveformReady, setWaveformReady] = useState(false);
  const [audioCache] = useState<{ [fileId: string]: string }>({});


  // Load comment counts for all audio files
  useEffect(() => {
    const loadCommentCounts = async () => {
      const counts: Record<string, { total: number; unresolved: number }> = {};
      for (const file of audioFiles) {
        try {
          const fileId = file.fileId || file.id;
          const comments = await listComments(trackId, fileId);
          const unresolved = comments.filter(c => !c.resolved).length;
          counts[fileId] = { total: comments.length, unresolved };
        } catch (error) {
          logError('Failed to load comment count', { trackId, fileId: file.fileId || file.id, error });
        }
      }
      setCommentCounts(counts);
    };

    if (audioFiles.length > 0) {
      loadCommentCounts();
    }
  }, [audioFiles, trackId]);

  // Use centralized WebSocket notifications hook
  useWebSocketNotifications({
    trackId: trackId,
    fileType: 'audio', // Only process audio upload notifications
    onUploadUpdate: (notification) => {
      // Handle progress updates from WebSocket (redundant with direct progress callbacks)
      // This ensures we get updates even if progress callback doesn't fire
      const { upload_id, progress, filename } = notification;
      logInfo('WebSocket progress update', { 
        upload_id, 
        progress, 
        filename,
        hasProgress: progress !== undefined 
      });
      
      if (upload_id && progress !== undefined) {
        setUploadProgress(prev => {
          const current = prev[upload_id];
          const newProgress = {
            filename: filename || current?.filename || prev[upload_id]?.filename || 'Unknown file',
            progress: progress
          };
          
          logInfo('Updating progress from WebSocket', { 
            upload_id, 
            progress,
            filename: newProgress.filename,
            hadPrevious: !!current
          });
          
          return {
            ...prev,
            [upload_id]: newProgress
          };
        });
      }
    },
    onUploadComplete: (notification) => {
      const { upload_id, file_id, filename } = notification;
      
      // Upload completed successfully - we only need file_id for the streaming endpoint
      setUploadingFiles(prev => {
        const newSet = new Set(prev);
        newSet.delete(upload_id);
        logInfo('Removed completed upload from set', { upload_id, newSet: Array.from(newSet) });
        return newSet;
      });
      
      // Clean up progress tracking
      setUploadProgress(prev => {
        const newState = { ...prev };
        delete newState[upload_id];
        return newState;
      });
      setUploadFilenames(prev => {
        const newState = { ...prev };
        delete newState[upload_id];
        return newState;
      });
      
      // Add the new audio file to the list
      const newAudioFile: AudioFile = {
        id: file_id || '',
        name: filename || 'Unknown file',
        fileId: file_id || '',
        isPreview: false // New files are not preview by default
      };
      
      logInfo('Adding new audio file to list', { newAudioFile, currentAudioFiles: audioFiles });
      onAudioFilesChange([...audioFiles, newAudioFile]);
      
      // Note: Toast is shown by useWebSocketNotifications hook
    },
    onUploadFailed: (notification) => {
      const { upload_id, error } = notification;
      
      setUploadingFiles(prev => {
        const newSet = new Set(prev);
        newSet.delete(upload_id);
        return newSet;
      });
      
      // Clean up progress tracking
      setUploadProgress(prev => {
        const newState = { ...prev };
        delete newState[upload_id];
        return newState;
      });
      setUploadFilenames(prev => {
        const newState = { ...prev };
        delete newState[upload_id];
        return newState;
      });
      
      // Note: Toast is shown by useWebSocketNotifications hook
    },
    onAuthIssue: (notification) => {
      // Handle Google Drive auth issues - the hook will automatically trigger re-auth
      logInfo('🔐 AUTH ISSUE: AudioFilesSection received auth issue notification', { 
        notification,
        trackId: trackId,
        component: 'AudioFilesSection'
      });
    },
    unlinkGoogleDrive: unlinkGoogleDrive
  });

  // Cleanup blob URLs when component unmounts
  useEffect(() => {
    return () => {
      // Revoke all cached blob URLs on unmount
      Object.values(audioCache).forEach(url => {
        if (url) URL.revokeObjectURL(url);
      });
      // Revoke current blob URL if different from cached ones
      if (blobUrl && !Object.values(audioCache).includes(blobUrl)) {
        URL.revokeObjectURL(blobUrl);
      }
    };
  }, []);

  const handleTogglePreview = async (fileId: string, makePreview: boolean) => {
    try {
      const user = getAuth().currentUser;
      if (!user) {
        console.error('No authenticated user found');
        throw new Error('No user');
      }
      
      // Check if user is authenticated
      if (!user.emailVerified && user.email) {
        console.warn('User email not verified');
      }
      
      const token = await user.getIdToken(true); // Force refresh token
      
      if (!token) {
        console.error('Failed to get ID token');
        throw new Error('Failed to get authentication token');
      }
      
      console.log('Preview toggle request:', {
        trackId,
        fileId,
        makePreview,
        hasToken: !!token,
        tokenLength: token?.length,
        userEmail: user.email,
        userUid: user.uid
      });
      
      // Call backend API to toggle preview status
      const response = await fetch(getApiUrl(API_CONFIG.ENDPOINTS.AUDIO_PREVIEW(trackId, fileId)), {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ isPreview: makePreview }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Preview toggle failed:', {
          status: response.status,
          statusText: response.statusText,
          error: errorText
        });
        throw new Error(`Failed to update preview status: ${response.status} ${response.statusText}`);
      }

      // Update local state
      const updatedFiles = audioFiles.map(file => ({
        ...file,
        isPreview: (file.fileId || file.id) === fileId ? makePreview : false // Only one file can be preview
      }));

      onAudioFilesChange(updatedFiles);
      
      if (makePreview) {
        toast.success(t('track.audioFileMarkedAsPreview'));
      } else {
        toast.success(t('track.audioFileUnmarkedAsPreview'));
      }
    } catch (error) {
      logError('Failed to toggle preview status', { trackId, fileId, error });
      toast.error(t('track.previewToggleError'));
    }
  };

  // Play: start playing a file
  const handlePlay = async (idx: number, fileId: string) => {
    if (!fileId) {
      toast.error(t('audio.noFileId'));
      return;
    }
    
    // If we're already playing this file, just unpause
    if (playingIdx === idx) {
      setIsPaused(false);
      return;
    }

    setPlayingIdx(idx);
    setIsPaused(true);
    setLoadingIdx(idx);
    setWaveformReady(false);
    setBlobUrl('');

    try {
      if (audioCache[fileId]) {
        setBlobUrl(audioCache[fileId]);
        setIsPaused(false);
        return;
      }

      const access = await resolveTrackMediaUrl(trackId, fileId, 'audio', storageProvider);
      audioCache[fileId] = access.url;
      setBlobUrl(access.url);
      setIsPaused(false);
    } catch (e) {
      logError('Error loading audio', { error: e instanceof Error ? e.message : String(e), trackId, fileId });
      toast.error(t('audio.loadError'));
      setPlayingIdx(null);
      setBlobUrl('');
      setLoadingIdx(null);
    }
  };

  // Pause: pause current playback
  const handlePause = () => {
    if (playingIdx !== null) {
      setIsPaused(true);
    }
  };

  // Continue: unpause current playback
  const handleContinue = () => {
    if (playingIdx !== null) {
      setIsPaused(false);
    }
  };

  // Stop: stop playback completely
  const handleStop = () => {
    setPlayingIdx(null);
    setIsPaused(false);
    setLoadingIdx(null);
    setWaveformReady(false);
    // Don't revoke blob URL here - let it stay for potential replay
  };

  // Download: download archive file directly from Google Drive
  const handleDownload = (fileId: string, fileName: string) => {
    try {
      // Use Google Drive direct download URL
      // For large files, we use the confirm=t parameter to bypass virus scan warning
      const downloadUrl = `https://drive.google.com/uc?export=download&id=${fileId}&confirm=t`;
      
      // Create download link that opens in new tab/window
      // This will trigger browser's download behavior
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success(t('track.fileDownloaded') || 'File download started');
    } catch (error) {
      logError('Failed to download file', { trackId, fileId, fileName, error });
      toast.error(t('track.fileDownloadError') || 'Failed to download file');
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    validateAndSetFiles(files);
  };

  const handleUploadSelected = async () => {
    if (selectedFiles.length === 0) return;
    
    setIsUploading(true);
    
    // Declare progress tracking variables outside try block for cleanup access
    let simulatedProgressInterval: NodeJS.Timeout | null = null;
    
    try {
      // Import chunk calculation helper
      const { calculateChunkCount } = await import('../../services/chunkUploadService');
      
      // Calculate total chunks across all files upfront
      const fileChunkCounts = selectedFiles.map(file => ({
        file,
        chunkCount: calculateChunkCount(file)
      }));
      const totalChunks = fileChunkCounts.reduce((sum, item) => sum + item.chunkCount, 0);
      
      logInfo('Starting sequential upload', {
        totalFiles: selectedFiles.length,
        totalChunks,
        fileChunkCounts: fileChunkCounts.map(item => ({ 
          fileName: item.file.name, 
          chunkCount: item.chunkCount 
        }))
      });
      
      // Create a single upload ID for overall progress tracking
      const overallUploadId = `overall-upload-${Date.now()}`;
      const allFileNames = selectedFiles.map(f => f.name).join(', ');
      
      // Initialize overall progress immediately
      setUploadingFiles(new Set([overallUploadId]));
      setUploadFilenames({ [overallUploadId]: allFileNames });
      setUploadProgress({ 
        [overallUploadId]: { filename: allFileNames, progress: 0 } 
      });
      
      // Initialize file statuses - all start as 'uploading'
      const initialStatuses: Record<string, 'uploading' | 'completed' | 'failed'> = {};
      selectedFiles.forEach(file => {
        initialStatuses[file.name] = 'uploading';
      });
      setUploadFileStatuses(initialStatuses);
      
      // Track successful and failed uploads
      const successfulUploads: string[] = [];
      const failedUploads: string[] = [];
      
      // Calculate progress per chunk (as percentage) - constant across all files
      const progressPerChunk = 100 / totalChunks;
      
      // Simulated progress tracking - MUST be outside file loop to persist across files
      let currentSimulatedProgress = 0; // Global progress across all chunks
      let targetProgress = 0; // Target progress for current chunk being uploaded
      
      // Helper to update progress (snaps to target if ahead, or uses simulated if behind)
      const updateProgress = () => {
        // If simulated progress is ahead of target, use target (we caught up)
        // Otherwise use simulated progress (showing movement)
        const displayProgress = Math.min(currentSimulatedProgress, targetProgress);
        // Use actual progress value (with decimals) for smooth sliding, not rounded
        const clamped = Math.min(displayProgress, 99);
        
        setUploadProgress(prev => ({
          ...prev,
          [overallUploadId]: {
            filename: allFileNames,
            progress: clamped
          }
        }));
      };
      
      // Start simulated progress timer (1% per second = 0.2% per 200ms)
      const startSimulatedProgress = () => {
        if (simulatedProgressInterval) {
          clearInterval(simulatedProgressInterval);
        }
        
        simulatedProgressInterval = setInterval(() => {
          // Only increase if we haven't reached the target yet
          if (currentSimulatedProgress < targetProgress) {
            currentSimulatedProgress += 0.2; // 0.2% per 200ms = 1% per second
            updateProgress();
          } else {
            // Reached target, stop the interval (will restart when next chunk completes)
            if (simulatedProgressInterval) {
              clearInterval(simulatedProgressInterval);
              simulatedProgressInterval = null;
            }
          }
        }, 200); // Update every 200ms for smoother animation
      };
      
      // Cleanup function for simulated progress
      const cleanupSimulatedProgress = () => {
        if (simulatedProgressInterval) {
          clearInterval(simulatedProgressInterval);
          simulatedProgressInterval = null;
        }
      };
      
      // Initialize progress - start from 0%
      updateProgress();
      startSimulatedProgress();
      
      // Upload files sequentially (one at a time)
      for (let fileIndex = 0; fileIndex < selectedFiles.length; fileIndex++) {
        const file = selectedFiles[fileIndex];
        const fileChunkCount = fileChunkCounts[fileIndex].chunkCount;
        
        try {
          logInfo(`Uploading file ${fileIndex + 1}/${selectedFiles.length}`, {
            fileName: file.name,
            chunkCount: fileChunkCount,
            totalChunks
          });
          
          // Use async upload service which handles chunked uploads for large files
          const { asyncUploadService } = await import('../../services/asyncUploadService');
          
          // Calculate chunks from previous files (base for current file)
          const previousFileChunks = fileChunkCounts
            .slice(0, fileIndex)
            .reduce((sum, item) => sum + item.chunkCount, 0);
          
          logInfo('Progress calculation setup', {
            fileName: file.name,
            fileChunkCount,
            totalChunks,
            previousFileChunks,
            progressPerChunk: progressPerChunk.toFixed(2),
            currentSimulatedProgress: currentSimulatedProgress.toFixed(2)
          });
          
          // Set initial target to first chunk completion of this file (if this file has chunks)
          if (fileChunkCount > 1) {
            targetProgress = (previousFileChunks + 1) * progressPerChunk; // First chunk of this file
          } else {
            targetProgress = (previousFileChunks + 1) * progressPerChunk; // Or just the file itself if 1 chunk
          }
          
          logInfo('Starting file upload', {
            fileName: file.name,
            currentSimulatedProgress: currentSimulatedProgress.toFixed(2),
            targetProgress: targetProgress.toFixed(2),
            previousFileChunks
          });
          
          // Upload file with progress tracking
          const task = await asyncUploadService.uploadFile(
            trackId, 
            file, 
            'audio',
            // File-level progress (for small files using async upload - usually won't fire, but just in case)
            fileChunkCount === 1 ? (fileProgress) => {
              if (fileProgress >= 100) {
                // Small file completed - snap to its completion point
                targetProgress = (previousFileChunks + 1) * progressPerChunk;
                currentSimulatedProgress = targetProgress; // Snap to target
                updateProgress();
                cleanupSimulatedProgress();
              }
            } : undefined,
            // Chunk completion progress (for large files) - called when chunk is fully uploaded
            fileChunkCount > 1 ? (chunkIndex, totalChunksForFile) => {
              // chunkIndex is 1-based (number of completed chunks for this file)
              // When chunkIndex = 0, it's called before upload starts (we ignore it)
              // When chunkIndex = 1, first chunk completed
              // When chunkIndex = 2, second chunk completed, etc.
              
              if (chunkIndex === 0) {
                // Initial call before upload - target is already set to first chunk completion
                // Simulated progress will start from current position and move towards target
                return;
              }
              
              // A chunk just completed - calculate global chunk index
              const globalChunkIndex = previousFileChunks + chunkIndex;
              const chunkCompletionProgress = globalChunkIndex * progressPerChunk;
              
              logInfo('Chunk completed', {
                fileName: file.name,
                chunkIndex,
                totalChunksForFile,
                previousFileChunks,
                globalChunkIndex,
                chunkCompletionProgress: chunkCompletionProgress.toFixed(2),
                currentSimulatedProgressBefore: currentSimulatedProgress.toFixed(2),
                totalChunks
              });
              
              // Snap to chunk completion point (we reached this chunk boundary)
              currentSimulatedProgress = chunkCompletionProgress;
              targetProgress = chunkCompletionProgress;
              updateProgress();
              
              // If there's a next chunk (globally), set target to next chunk completion and restart simulation
              if (globalChunkIndex < totalChunks) {
                // Set target to next chunk completion
                targetProgress = (globalChunkIndex + 1) * progressPerChunk;
                logInfo('Setting target for next chunk', {
                  fileName: file.name,
                  nextGlobalChunkIndex: globalChunkIndex + 1,
                  nextTarget: targetProgress.toFixed(2),
                  currentProgress: currentSimulatedProgress.toFixed(2)
                });
                // Restart simulated progress from current position towards next chunk
                startSimulatedProgress();
              } else {
                // Last chunk of last file completed, stop simulation
                cleanupSimulatedProgress();
                currentSimulatedProgress = 100;
                targetProgress = 100;
                updateProgress();
              }
            } : undefined
          );
          
          // File upload completed - update progress to this file's completion point
          const fileCompletionProgress = (previousFileChunks + fileChunkCount) * progressPerChunk;
          currentSimulatedProgress = fileCompletionProgress;
          targetProgress = fileCompletionProgress;
          updateProgress();
          
          // If this is not the last file, continue simulation towards next file's first chunk
          const isLastFile = fileIndex === selectedFiles.length - 1;
          if (!isLastFile) {
            // Set target to first chunk of next file
            const nextFileFirstChunk = previousFileChunks + fileChunkCount + 1;
            if (nextFileFirstChunk <= totalChunks) {
              targetProgress = nextFileFirstChunk * progressPerChunk;
              startSimulatedProgress();
            }
          } else {
            // Last file completed, stop simulation (will be set to 100% at the end)
            cleanupSimulatedProgress();
          }
          
          logInfo('File upload completed', {
            fileName: file.name,
            taskId: task.id,
            fileChunkCount,
            totalChunks
          });
          
          // Mark file as completed
          setUploadFileStatuses(prev => ({
            ...prev,
            [file.name]: 'completed'
          }));
          
          successfulUploads.push(file.name);
        } catch (uploadError) {
          // Clean up simulated progress interval on error
          cleanupSimulatedProgress();
          
          const errorMessage = uploadError instanceof Error ? uploadError.message : String(uploadError);
          const errorDetails = uploadError instanceof Error ? { 
            name: uploadError.name, 
            message: uploadError.message, 
            stack: uploadError.stack 
          } : { error: String(uploadError) };
          
          console.error('❌ Upload failed:', {
            fileName: file.name,
            trackId,
            error: errorMessage,
            fullError: uploadError,
            errorDetails
          });
          
          logError('Failed to upload file', { 
            trackId, 
            fileName: file.name, 
            error: errorMessage,
            errorDetails
          });
          
          // Mark file as failed
          setUploadFileStatuses(prev => ({
            ...prev,
            [file.name]: 'failed'
          }));
          
          failedUploads.push(file.name);
          // Continue with next file even if this one failed
        }
      }
      
      // All files completed - clean up and set to 100%
      cleanupSimulatedProgress();
      currentSimulatedProgress = 100;
      targetProgress = 100;
      setUploadProgress(prev => ({
        ...prev,
        [overallUploadId]: {
          filename: allFileNames,
          progress: 100
        }
      }));
      
      // Clean up overall upload ID after a short delay (let UI show 100%)
      setTimeout(() => {
        setUploadingFiles(prev => {
          const newSet = new Set(prev);
          newSet.delete(overallUploadId);
          return newSet;
        });
        setUploadFilenames(prev => {
          const newState = { ...prev };
          delete newState[overallUploadId];
          return newState;
        });
        setUploadProgress(prev => {
          const newState = { ...prev };
          delete newState[overallUploadId];
          return newState;
        });
        // Clear file statuses
        setUploadFileStatuses({});
      }, 500);
      
      if (failedUploads.length > 0) {
        console.error('❌ Files failed to upload:', {
          failedCount: failedUploads.length,
          failedFiles: failedUploads,
          trackId,
          userId: user?.uid,
          totalFiles: selectedFiles.length,
          successfulFiles: successfulUploads
        });
        
        logError('Files failed to upload', { 
          failedUploads, 
          trackId,
          userId: user?.uid 
        });
        
        toast.error(`${failedUploads.length} file(s) failed to upload: ${failedUploads.join(', ')}`);
      }
      
      // Clear selected files and hide upload area
      setSelectedFiles([]);
      
    } catch (error) {
      // Clean up progress interval if it exists
      if (simulatedProgressInterval) {
        clearInterval(simulatedProgressInterval);
        simulatedProgressInterval = null;
      }
      
      // Never fail silently - always log to console
      const errorMessage = error instanceof Error ? error.message : String(error);
      console.error('❌ Critical error in upload handler:', {
        error: errorMessage,
        fullError: error,
        trackId,
        userId: user?.uid,
        selectedFilesCount: selectedFiles.length
      });
      
      logError('Failed to queue audio uploads', { trackId, error: errorMessage });
      toast.error('Failed to start uploads');
    } finally {
      setIsUploading(false);
    }
  };

  const validateAndSetFiles = (files: File[]) => {
    const { validFiles, errors } = filterFilesForCategory(files, 'audio');
    const hint = UPLOAD_FILE_ACCEPT.audio.hint;

    if (errors.length > 0) {
      const errorMessage =
        errors.length === 1
          ? `${errors[0]}. Supported: ${hint}.`
          : `${errors.length} invalid files. Supported: ${hint}.`;
      toast.error(errorMessage);
    }

    if (validFiles.length > 0) {
      setSelectedFiles(validFiles);
      if (validFiles.length < files.length) {
        toast.success(`${validFiles.length} valid file(s) selected`);
      }
    } else if (files.length > 0) {
      setSelectedFiles([]);
    }
  };

  const clearSelectedFiles = () => {
    setSelectedFiles([]);
  };

  if (binaryAccess === false) {
    return (
      <BinaryUnavailablePlaceholder
        reason={binaryInactiveReason}
        requiredProvider={requiredStorageProvider}
        viewerRole={viewerRole}
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Upload Area */}
      <div className="relative w-full mb-6">
        {audioFolderUrl && (
          <a
            href={audioFolderUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="absolute top-2 right-2 text-gray-400 hover:text-blue-600 z-10"
            title={t('track.openCloudFolder')}
            style={{ lineHeight: 0 }}
          >
            <FaLink size={24} />
          </a>
        )}
        
        {permissions?.canUploadAudio ? (
          <FileUpload
            label={isUploading ? t('fileUpload.uploading') : t('fileUpload.dropAudioFiles')}
            fileCategory="audio"
            multiple
            showPreview={false}
            disabled={isUploading}
            onFile={(files) => validateAndSetFiles(Array.isArray(files) ? files : Array.from(files))}
            data-testid="audio-file-input"
          />
        ) : (
          <div className="border-2 border-dashed rounded-lg p-6 text-center opacity-50" style={{ borderColor: 'var(--color-border)' }}>
            <div>
              <p className="text-sm" style={{ color: 'var(--color-muted)' }}>
                Upload not available - View only access
              </p>
              <p className="text-xs mt-1" style={{ color: 'var(--color-muted)' }}>
                You can play existing audio files below
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Selected Files Preview - Hidden only after upload button is clicked */}
      {selectedFiles.length > 0 && permissions?.canUploadAudio && !isUploading && (
        <div className="mb-4 p-4 rounded-lg border" style={{ backgroundColor: 'var(--color-hover)', borderColor: 'var(--color-border)' }}>
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-medium" style={{ color: 'var(--color-text)' }}>
              {selectedFiles.length} file(s) selected
            </h4>
            <button
              onClick={clearSelectedFiles}
              className="text-sm hover:opacity-80 transition-opacity"
              style={{ color: 'var(--brand-from)' }}
            >
              {t('fileUpload.clear')}
            </button>
          </div>
          <div className="space-y-2">
            {selectedFiles.map((file, idx) => (
              <div key={idx} className="flex items-center justify-between text-sm">
                <span className="truncate" style={{ color: 'var(--color-text)' }}>{file.name}</span>
                <span style={{ color: 'var(--color-muted)' }}>{(file.size / 1024 / 1024).toFixed(1)} MB</span>
              </div>
            ))}
          </div>
          <button
            onClick={handleUploadSelected}
            disabled={isUploading}
            className="mt-3 w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="audio-upload-button"
          >
            {isUploading ? 'Uploading...' : `Upload ${selectedFiles.length} File(s)`}
          </button>
        </div>
      )}

      {/* Huge CassetteLoader when uploading */}
      {isUploading && (
        <div className="mb-4 flex items-center justify-center py-12">
          <CassetteLoader size={120} variant="inline" />
        </div>
      )}

      {/* Upload Progress Display */}
      <UploadProgressDisplay
        uploadingFiles={uploadingFiles}
        uploadProgress={uploadProgress}
        uploadFilenames={uploadFilenames}
        uploadFileStatuses={uploadFileStatuses}
      />

      {/* Single player above the grid, controlled by tile buttons */}
      {playingIdx !== null && (
        <AudioWavePlayer
          key={audioFiles[playingIdx]?.fileId || audioFiles[playingIdx]?.id}
          trackId={trackId}
          fileId={audioFiles[playingIdx]?.fileId || audioFiles[playingIdx]?.id || ''}
          fileName={audioFiles[playingIdx]?.name}
          blobUrl={blobUrl}
          isPaused={isPaused}
          isLoading={loadingIdx === playingIdx && !waveformReady}
          storageProvider={storageProvider}
          onReady={() => {
            setWaveformReady(true);
            setLoadingIdx(null);
          }}
          onLoadError={() => {
            setWaveformReady(false);
            setLoadingIdx(null);
            setPlayingIdx(null);
            setBlobUrl('');
          }}
          onPause={handlePause}
          onContinue={handleContinue}
          onStop={handleStop}
          loadingText={t('audio.loading')}
          canComment={permissions?.canAddAudioComments || false}
          canView={true}
          showCommentTiles={true}
          onCommentCountChange={(fileId, count) => {
            setCommentCounts(prev => {
              const current = prev[fileId] || { total: 0, unresolved: 0 };
              // Update total count
              // For unresolved: if count increased, assume the new comment(s) are unresolved
              const countDiff = count - current.total;
              const unresolved = countDiff > 0 
                ? current.unresolved + countDiff  // New comments are unresolved
                : Math.max(0, current.unresolved + countDiff); // Count decreased (comment deleted)
              return { ...prev, [fileId]: { total: count, unresolved } };
            });
          }}
        />
      )}

      {/* Audio Files Grid */}
      {audioFiles.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 mt-6">
          {audioFiles.map((file, idx) => {
            const isPreview = !!file.isPreview;
            const ext = file.name.split('.').pop()?.toLowerCase();
            const isArchive = ext === 'zip' || ext === 'rar' || ext === '7z';
            let icon = null;
            
            // For archive files, show archive icon
            if (isArchive) {
              icon = (
                <div className="w-12 h-12 flex items-center justify-center" title="Archive">
                  <Archive className="w-8 h-8" style={{ color: 'var(--color-text)' }} />
                </div>
              );
            } else {
              // Build a colored circular badge with filetype label for audio files
              const fileLabel = (ext === 'mp3' || ext === 'wav') ? ext.toUpperCase() : (ext || '').toUpperCase();
              let colorClass = 'bg-gray-300';
              let textColorClass = 'text-gray-700';
              if (ext === 'mp3') {
                colorClass = isPreview ? 'bg-blue-500' : 'bg-blue-400';
                textColorClass = 'text-white';
              } else if (ext === 'wav') {
                colorClass = isPreview ? 'bg-red-500' : 'bg-red-400';
                textColorClass = 'text-white';
              }
              icon = (
                <div className={`w-8 h-8 rounded-full ${colorClass} flex items-center justify-center ${textColorClass} font-bold text-[10px]`}
                     title={fileLabel || undefined}>
                  {fileLabel || ''}
                </div>
              );
            }
            
            const isDeleting = deletingFiles.has(file.id);
            
            return (
              <div key={file.id} className={`rounded-xl border p-4 flex flex-col items-center relative hover:shadow-md transition-all duration-200 group ${isDeleting ? 'opacity-50 pointer-events-none' : ''}`} style={{ backgroundColor: 'var(--color-hover)', borderColor: 'var(--color-border)' }}>
                {/* Delete loading overlay */}
                {isDeleting && (
                  <div className="absolute inset-0 flex items-center justify-center z-20 rounded-xl bg-black/50 backdrop-blur-sm">
                    <div className="flex flex-col items-center gap-3">
                      <CassetteLoader size={48} variant="inline" />
                      <span className="text-sm font-medium text-white">
                        {t('track.deletingAudioFile')}
                      </span>
                    </div>
                  </div>
                )}
                
                {isPreview && (
                  <span className="absolute -top-2 left-1/2 transform -translate-x-1/2 bg-blue-500 text-white text-xs px-2 py-1 rounded-full font-semibold z-10 shadow-sm">
                    Preview
                  </span>
                )}
                
                <div className="mb-3 mt-2 relative">{icon}</div>
                
                <div className="text-sm font-medium text-center mb-3 line-clamp-2 w-full" style={{ color: 'var(--color-text)' }} title={file.name}>
                  {file.name}
                </div>
                
                {/* Preview toggle button - only for non-archive files */}
                {!isArchive && permissions?.canUploadAudio && (
                  <button
                    className={`absolute top-2 left-2 w-6 h-6 flex items-center justify-center rounded-full border transition-all ${
                      isPreview 
                        ? 'bg-blue-500 border-blue-500 text-white hover:bg-blue-600' 
                        : ''
                    }`}
                    style={isPreview ? undefined : { backgroundColor: 'var(--color-surface)', borderColor: 'var(--color-border)', color: 'var(--color-text)' }}
                    title={isPreview ? t('track.unmarkPreview') : t('track.markAsPreview')}
                    onClick={() => handleTogglePreview(file.fileId || file.id, !isPreview)}
                  >
                    {isPreview ? (
                      <Check className="w-4 h-4" />
                    ) : (
                      <Star className="w-4 h-4" />
                    )}
                  </button>
                )}

                {/* Remove button */}
                {permissions?.canUploadAudio && !isDeleting && (
                  <button
                    className="absolute top-2 right-2 w-6 h-6 flex items-center justify-center rounded-full transition-all"
                    style={{ backgroundColor: 'var(--color-surface)', border: '1px solid var(--color-border)', color: '#ef4444' }}
                    title={t('track.removeAudioFile')}
                    onClick={() => {
                      if (window.confirm(t('track.confirmRemoveAudioFile'))) {
                        setDeletingFiles(prev => new Set(prev).add(file.id));
                        deleteAudioFile(trackId, file.fileId || file.id)
                          .then(() => {
                            const updatedFiles = audioFiles.filter((_, i) => i !== idx);
                            onAudioFilesChange(updatedFiles);
                            toast.success(t('track.audioFileRemoved'));
                          })
                          .catch(err => {
                            logError('Failed to delete audio file', { trackId, fileId: file.id, error: err });
                            toast.error(t('track.audioFileRemoveError'));
                          })
                          .finally(() => {
                            setDeletingFiles(prev => {
                              const newSet = new Set(prev);
                              newSet.delete(file.id);
                              return newSet;
                            });
                          });
                      }
                    }}
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
                
                {/* Play/Pause controls - only show for non-archive files */}
                {!isArchive && (
                  <div className="flex items-center justify-center gap-2 mt-auto">
                    {loadingIdx === idx ? (
                      // Loading state - show spinner in play button
                      <button 
                        className="w-8 h-8 flex items-center justify-center rounded-full border text-gray-500" 
                        style={{ backgroundColor: 'var(--color-surface)', borderColor: 'var(--color-border)' }}
                        disabled
                      >
                        <CassetteLoader size={48} variant="inline" />
                      </button>
                    ) : playingIdx === idx ? (
                      // Playing state - show pause/stop buttons
                      <>
                        {!isPaused ? (
                          <button className="w-8 h-8 flex items-center justify-center rounded-full border transition text-blue-600 relative" style={{ backgroundColor: 'var(--color-surface)', borderColor: '#60a5fa' }} onClick={handlePause} aria-label={t('track.pause')}>
                            <Pause className="w-4 h-4" />
                          </button>
                        ) : (
                          <button className="w-8 h-8 flex items-center justify-center rounded-full border transition text-blue-600 relative" style={{ backgroundColor: 'var(--color-surface)', borderColor: '#60a5fa' }} onClick={handleContinue} aria-label={t('track.continue')}>
                            <Play className="w-4 h-4" />
                            <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 bg-blue-600 rounded-full" />
                          </button>
                        )}
                        <button className="w-8 h-8 flex items-center justify-center rounded-full border transition text-red-500" style={{ backgroundColor: 'var(--color-surface)', borderColor: 'var(--color-border)' }} onClick={handleStop} aria-label={t('track.stop')}>
                          <Square className="w-4 h-4" />
                        </button>
                      </>
                    ) : (
                      // Stopped state - show play button
                      <button 
                        className="w-8 h-8 flex items-center justify-center rounded-full border transition text-gray-500 hover:text-blue-600" 
                        style={{ backgroundColor: 'var(--color-surface)', borderColor: 'var(--color-border)' }}
                        onClick={() => {
                          handlePlay(idx, file.fileId || file.id);
                        }} 
                        aria-label={t('track.play')}
                      >
                        <Play className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                )}

                {/* Download button - only show for archive files */}
                {isArchive && (
                  <div className="flex items-center justify-center gap-2 mt-auto">
                    <button 
                      className="w-10 h-10 flex items-center justify-center rounded-full border transition text-blue-600 hover:bg-blue-50" 
                      style={{ backgroundColor: 'var(--color-surface)', borderColor: 'var(--color-border)' }}
                      onClick={() => handleDownload(file.fileId || file.id, file.name)}
                      aria-label={t('track.download') || 'Download file'}
                      title={t('track.download') || 'Download file'}
                    >
                      <Download className="w-5 h-5" />
                    </button>
                  </div>
                )}

                {/* Comments section - responsive behavior - only for non-archive files */}
                {!isArchive && (
                  <div className="w-full mt-2">
                    {/* Mobile: Single button that goes to comments page */}
                    <button
                      data-testid="audio-comments-mobile-button"
                      className="w-full py-1 px-2 text-xs rounded flex items-center justify-center gap-1 transition-colors sm:hidden"
                      style={{ 
                        backgroundColor: 'var(--color-surface)', 
                        color: 'var(--color-text)',
                        border: '1px solid var(--color-border)'
                      }}
                      onClick={() => navigate(audioCommentsPagePath(trackId, file.fileId || file.id))}
                      title={t('audioComments.viewAllComments')}
                    >
                      <MessageCircle className="w-3 h-3" />
                      <span className="truncate">
                        {(() => {
                          const counts = commentCounts[file.fileId || file.id];
                          if (!counts) return `0 ${t('audioComments.comments')}`;
                          if (counts.total === 0) return `0 ${t('audioComments.comments')}`;
                          if (counts.unresolved === counts.total) return `${counts.total} ${t('audioComments.comments')}`;
                          return `(${counts.unresolved}/${counts.total}) ${t('audioComments.comments')}`;
                        })()}
                      </span>
                    </button>

                    {/* Desktop: Split button with inline comments + arrow */}
                    <div className="hidden sm:flex">
                      {/* Main comments button */}
                      <button
                        data-testid="audio-comments-desktop-main-button"
                        className="flex-1 py-1 px-2 text-xs rounded-l flex items-center justify-center gap-1 transition-colors"
                        style={{ 
                          backgroundColor: 'var(--color-surface)', 
                          color: 'var(--color-text)',
                          border: '1px solid var(--color-border)',
                          borderRight: 'none'
                        }}
                        onClick={async () => {
                          // Auto-open player for this file (paused at 0:00)
                          const fileIndex = audioFiles.findIndex(f => (f.fileId || f.id) === (file.fileId || file.id));
                          await handlePlay(fileIndex, file.fileId || file.id);
                          setIsPaused(true); // Pause immediately after loading
                        }}
                        title={t('audioComments.viewComments')}
                      >
                        <MessageCircle className="w-3 h-3" />
                        <span className="truncate">
                          {(() => {
                            const counts = commentCounts[file.fileId || file.id];
                            if (!counts) return `0 ${t('audioComments.comments')}`;
                            if (counts.total === 0) return `0 ${t('audioComments.comments')}`;
                            if (counts.unresolved === counts.total) return `${counts.total} ${t('audioComments.comments')}`;
                            return `(${counts.unresolved}/${counts.total}) ${t('audioComments.comments')}`;
                          })()}
                        </span>
                      </button>
                      
                      {/* Arrow button to go to comments page */}
                      <button
                        data-testid="audio-comments-desktop-arrow-button"
                        className="py-1 px-2 text-xs rounded-r flex items-center justify-center transition-colors"
                        style={{ 
                          backgroundColor: 'var(--color-surface)', 
                          color: 'var(--color-text)',
                          border: '1px solid var(--color-border)'
                        }}
                        onClick={() => navigate(audioCommentsPagePath(trackId, file.fileId || file.id))}
                        title={t('audioComments.viewAllComments')}
                      >
                        <ChevronRight className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {audioFiles.length === 0 && uploadingFiles.size === 0 && (
        <p className="text-gray-500 text-center py-8">
                          {t('audio.noFilesYet')}
        </p>
      )}
    </div>
  );
};

export default AudioFilesSection; 